import React, { useEffect, useRef } from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils.ts';

const PrimitiveCheckbox = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & {
    indeterminate?: boolean;
    /** Overrides the wrapper styles — e.g. `mt-0` to drop the form-label nudge. */
    wrapperClassName?: string;
  }
>(
  (
    { className, wrapperClassName, checked, indeterminate = false, ...props },
    ref,
  ) => {
    const innerRef = useRef<HTMLInputElement | null>(null);

    // `indeterminate` is a DOM property, not an attribute — set it imperatively.
    useEffect(() => {
      if (innerRef.current) {
        innerRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    return (
      <div
        className={cn('relative isolate mt-0.5 inline-flex', wrapperClassName)}
      >
        <input
          ref={(node) => {
            innerRef.current = node;
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          type="checkbox"
          checked={checked}
          className={cn(
            'peer !mt-0 size-4 min-h-4 min-w-4 appearance-none rounded-[3px] border border-neutral-600 bg-white',
            'cursor-pointer outline-none transition-colors duration-200',
            'checked:border-accent-600 checked:bg-accent-600',
            'indeterminate:border-accent-600 indeterminate:bg-accent-600',
            'disabled:cursor-not-allowed disabled:border-basic-disabled-main disabled:bg-[#F2F2F2]',
            'disabled:checked:border-basic-disabled-main disabled:checked:bg-basic-disabled-main',
            className,
          )}
          {...props}
        />
        <Check className="pointer-events-none absolute inset-0 mx-auto my-auto hidden size-[14px] stroke-[3] text-white peer-checked:block" />
        {indeterminate && (
          <Minus className="pointer-events-none absolute inset-0 mx-auto my-auto size-[14px] stroke-[3] text-white" />
        )}
      </div>
    );
  },
);

PrimitiveCheckbox.displayName = 'PrimitiveCheckbox';

export default PrimitiveCheckbox;
